import { useEffect, useState } from "react";
import type { ExpandedModel } from "../data/models";
import { INTERNAL_MODEL_BASE_URL } from "../env";
import { modelShouldBeHidden } from "../lib/utils";
import { useCachedRequest } from "./use-cached-request";

export const MODELS_URL =
  process.env.NEXT_PUBLIC_MOCK_API === "true"
    ? "/api/mock/openai/v1/models"
    : `${INTERNAL_MODEL_BASE_URL}/models`;

export type ModelType = "text" | "audio";

export function useTextModels() {
  const { data, isLoading, error, mutate, isValidating } = useCachedRequest<{
    data: ExpandedModel[];
  }>({
    url: MODELS_URL,
    swrOptions: {
      revalidateOnFocus: true,
    },
  });

  if (!data) {
    return { data, isLoading, error, mutate, isValidating };
  }
  const specDecModel = data.data.find(
    (model) => model.id === "llama-3.1-70b-specdec",
  );

  const textModels = data.data.reduce((models, model) => {
    // this should be replaced by two backend fields
    if (
      !model.id.includes("allam-2-7b") &&
      model.id !== "whisper-large-v3----foo"
    ) {
      const expandedModel = {
        ...model,
        description: modelIdDescriptionMap[model.id],
        timestamp: modelIdTimestampMap[model.id],
        deprecated: false,
      };
      if (expandedModel?.metadata?.display_name) {
        expandedModel.metadata.display_name =
          model.metadata?.display_name?.trim();
      }
      if (
        model.id === "llama-3.2-11b-text-preview" ||
        model.id === "llama-3.2-90b-text-preview"
      ) {
        expandedModel.deprecated = true;
      }
      models.push(expandedModel);
    }
    return models;
  }, [] as ExpandedModel[]);

  if (!specDecModel) {
    textModels.push({
      id: "llama-3.1-70b-specdec",
      metadata: {
        display_name: "Llama 3.1 70b SpecDec",
      },
      created: 1709251200,
      owned_by: "Meta",
      context_window: 8192,
      active: true,
      description: modelIdDescriptionMap["llama-3.1-70b-specdec"],
      timestamp: modelIdTimestampMap["llama-3.1-70b-specdec"],
      deprecated: false,
      terms_url: null,
      is_terms_required: false,
      is_terms_accepted: null,
      max_completion_tokens: 131072,
      release_stage: "production",
    });
  }

  return {
    data: {
      ...data,
      data: [...textModels.filter((model) => !modelShouldBeHidden(model.id))],
    },
    isLoading,
    error,
    mutate,
    isValidating,
  };
}

export function useModel(modelId: string | undefined) {
  const {
    data: models,
    isLoading,
    error,
    mutate,
    isValidating,
  } = useTextModels();
  const [model, setModel] = useState<ExpandedModel | undefined>();
  useEffect(() => {
    if (!isValidating && modelId && models) {
      const modelComparison = models.data.find((m) => m.id === modelId);
      if (JSON.stringify(modelComparison) !== JSON.stringify(model)) {
        setModel(modelComparison as ExpandedModel);
      }
    }
  }, [isValidating, modelId]);
  return {
    data: model,
    isLoading,
    error,
    mutate,
    isValidating,
  };
}

const modelIdDescriptionMap: Record<string, string> = {
  "gemma-7b-it":
    "V1.1 7B parameter version of Google's Gemma models - a family of lightweight, state-of-the-art language models from Google, with open weights, and pre-trained variants.",
  "gemma2-9b-it":
    "V2 9B parameter version of Google's Gemma models - a family of lightweight, state-of-the-art language models from Google, with open weights, and pre-trained variants.",
  "mixtral-8x7b-32768":
    "A high-quality sparse mixture of experts model (SMoE) with open weights that handles a context of 32K tokens.",
  "groq-mock-slow-model": "A slow model for testing purposes.",
  "llama-3.1-70b-specdec":
    "Llama 3.1 70b running with Speculative Decode for the fastest output TPS. 8k context window. Only available to paying organizations.",
  "llama-3.1-8b-instant":
    "Llama 3.1 8b is a high-speed, instant-response model designed for efficient and rapid applications.",
  "llama-3.1-70b-versatile":
    "Llama 3.1 70b is a versatile model suitable for a wide range of applications, including chatbots, question answering, and more.",
  "llama-3.1-405b-reasoning":
    "The largest model in the Llama family, Llama 3.1 405b is suitable for advanced workflows, complex problem solving and synthetic data generation",
  "llama-3.2-1b-preview":
    "An ultra-fast, compact model ideal for high-throughput applications, excelling in rapid text processing and real-time language tasks.",
  "llama-3.2-3b-preview":
    "A lightweight text-only model for low-latency inferencing, text summarization, classification, and translation tasks.",
  "llama-3.2-11b-text-preview": "",
  "llama-3.2-11b-vision-preview":
    "A cost-effective multimodal model offering fast text generation which strikes a balance between performance and affordability.",
  "llama-3.2-90b-text-preview": "",
  "llama-3.2-90b-vision-preview":
    "A multimodal model providing superior accuracy in complex visual reasoning and language tasks.",
  "llama-3.3-70b-versatile":
    "Llama 3.3 70b is suitable for chatbots and question answering with enhanced quality comparable to Llama 3.1 405B.",
  "llama-3.3-70b-specdec":
    "Llama 3.3 70b specdec is the Llama 3.3 70b model running with speculative decoding for the fastest tokens per second.",
};

const modelIdTimestampMap: Record<string, number> = {
  "meta-llama/llama-4-scout-17b-16e-instruct": 1743884557, // Apr 05, 2025
  "llama-3.3-70b-specdec": 1733505017, // December 09, 2024
  "llama-3.3-70b-versatile": 1733447754, // December 09, 2024
  "llama-3.2-90b-vision-preview": 1727285716, // September 25, 2024
  "llama-3.2-90b-text-preview": 1727285715, // September 25, 2024
  "llama-3.2-11b-vision-preview": 1727283005, // September 25, 2024
  "llama-3.2-11b-text-preview": 1727283004, // September 25, 2024
  "llama-3.2-3b-preview": 1727224290, // September 25, 2024
  "llama-3.2-1b-preview": 1727224268, // September 25, 2024
  "llama-3.1-70b-specdec": 1731628800, // November 14, 2024
  "llama-3.1-405b-reasoning": 1727001609, // July 23, 2024
  "llama-3.1-70b-versatile": 1727001608, // July 23, 2024
  "llama-3.1-8b-instant": 1727001607, // July 23, 2024
  "gemma2-9b-it": 1719446400, // June 27, 2024
  "gemma-7b-it": 1708473600, // February 21, 2024
  "mixtral-8x7b-32768": 1702252800, // December 11, 2023
};
